import { useMemo, useEffect } from 'react';
import { useTheme } from '@simplywallst/ui-core';
import { toPercentage, toCurrencyTruncated } from '@/utilities/formatting';
import { format } from 'date-fns';
import TimeSeries, {
  Line,
  Area,
  DataTable,
  Title,
  Value,
  Bar,
} from '@/components/TimeSeries';
import type { SeriesFilter } from '@/components/TimeSeries/hooks';
import { useTimeSeriesReducer } from '@/components/TimeSeries/hooks';
import { useChartDefinitionsContext } from '@/components/ChartDefinitionsContext';
import { useTracking } from 'react-tracking';
import { Wrapper } from './styled';
import type { TimeSeriesDataType } from '../../hooks/types';
import { normalizeCurrencyMinMax } from './utils';

enum Series {
  series01 = 'dividend yield',
  series02 = 'annual amount',
  series03 = 'earnings per share',
  series04 = 'dividend payments',
}

type DefaultFilter = {
  [key in Series]: boolean;
};
export interface Props {
  DPS: number[][];
  DPSMax: number;
  DPSMin: number;
  EPS: number[][];
  EPSMax: number;
  EPSMin: number;
  currencyISO: string;
  dividendYield: number[][];
  dividendYieldMax: number;
  dividendYieldMin: number;
  dayZero: number;
  defaultFilter?: DefaultFilter;
  hideToggleFilter?: boolean;
  divPaymentDetailSeriesData: TimeSeriesDataType;
  divPaymentDetailMin: number;
  divPaymentDetailMax: number;
  divPaymentDetailCurrencyISO: string;
  divPaymentDetailFreq: string;
  dateFormatter?: (date: number, formatStr: string) => string;
  labels?: {
    series01: string;
    series02: string;
    series03: string;
    series04: string;
    past: string;
    future: string;
    notApplicable: string;
    perYear: string;
  };
}

const filterOnPrioritySeries = (n: string, filter: SeriesFilter<Series>) => {
  if (n === Series.series02 && filter[Series.series01]) {
    return false;
  }
  if (n === Series.series04 && filter[Series.series01]) {
    return false;
  }
  if (
    n === Series.series03 &&
    (filter[Series.series01] || filter[Series.series02])
  ) {
    return false;
  }
  return filter[n];
};

const getValue = (
  key: string,
  value: number,
  currencyISO: string,
  divPaymentDetailCurrencyISO: string
) => {
  if (key === Series.series01) {
    return toPercentage(value, 1);
  }
  if (key === Series.series04) {
    return toCurrencyTruncated(value, 3, divPaymentDetailCurrencyISO);
  }
  return toCurrencyTruncated(value, 3, currencyISO);
};

function defaultDateFormatter(date: number, formatStr: string) {
  return format(date, formatStr);
}

const StabilityAndGrowthOfPayments = ({
  DPS,
  DPSMax,
  DPSMin,
  EPS,
  EPSMax,
  EPSMin,
  currencyISO,
  dayZero,
  dividendYield,
  dividendYieldMax,
  dividendYieldMin,
  defaultFilter,
  hideToggleFilter,
  divPaymentDetailSeriesData,
  divPaymentDetailMin,
  divPaymentDetailMax,
  divPaymentDetailCurrencyISO,
  divPaymentDetailFreq,
  dateFormatter = defaultDateFormatter,
  labels = {
    series01: 'dividend yield',
    series02: 'annual amount',
    series03: 'earnings per share',
    series04: 'dividend payments',
    past: 'Past',
    future: 'Analysts Forecasts',
    notApplicable: 'n/a',
    perYear: '/year',
  },
}: Props) => {
  const theme = useTheme();

  const { Track, trackEvent } = useTracking({
    type: 'track',
    subject: 'chart',
    modifier: 'StabilityAndGrowthOfPayments',
  });
  const {
    chart01Gradient01,
    chart01Gradient02,
    chartNEGGradient01,
    chartNEGGradient02,
  } = useChartDefinitionsContext();

  const data = useMemo(
    () => ({
      [Series.series01]: dividendYield,
      [Series.series04]: divPaymentDetailSeriesData,
      [Series.series02]: DPS,
      [Series.series03]: EPS,
    }),
    [dividendYield, DPS, EPS, divPaymentDetailSeriesData]
  );

  const {
    entities,
    keys,
    minimumX,
    maximumX,
    periods,
    series,
    filter,
    disabled,
    dispatch,
  } = useTimeSeriesReducer<Series>({
    data,
    dayZero,
    defaultFilter: defaultFilter as DefaultFilter,
    hideToggleFilter,
  });

  const { series01, series02, series03, series04 } = labels;
  const seriesWithTranslation = useMemo(() => {
    return [
      { key: Series.series01, label: series01 },
      { key: Series.series04, label: series04 },
      { key: Series.series02, label: series02 },
      { key: Series.series03, label: series03 },
    ];
  }, [series01, series02, series03, series04]);

  const { currencyMin, currencyMax } = useMemo(
    () =>
      normalizeCurrencyMinMax([
        {
          enabled: filter[Series.series02],
          min: DPSMin,
          max: DPSMax,
        },
        {
          enabled: filter[Series.series03],
          min: EPSMin,
          max: EPSMax,
        },
        {
          enabled: filter[Series.series04],
          min: divPaymentDetailMin,
          max: divPaymentDetailMax,
        },
      ]),
    [
      DPSMax,
      DPSMin,
      EPSMax,
      EPSMin,
      divPaymentDetailMax,
      divPaymentDetailMin,
      filter,
    ]
  );

  const minMaxY = {
    [Series.series01]: [dividendYieldMin, dividendYieldMax],
    [Series.series02]: [currencyMin, currencyMax],
    [Series.series03]: [currencyMin, currencyMax],
    [Series.series04]: [currencyMin, currencyMax],
  };

  useEffect(
    () =>
      dispatch({
        type: 'reset',
        payload: {
          data,
          dayZero,
          defaultFilter: defaultFilter as DefaultFilter,
        },
      }),
    [dispatch, data, dayZero, defaultFilter]
  );

  const colors = {
    [Series.series01]: theme.color.chart01,
    [Series.series02]: theme.color.chart05,
    [Series.series03]: theme.color.chart03,
    [Series.series04]: theme.color.chart02,
  };

  const gradients = {
    [Series.series01]: {
      fillPositive: chart01Gradient01,
      fillNegative: chartNEGGradient01,
      fillFuturePositive: chart01Gradient02,
      fillFutureNegative: chartNEGGradient02,
    },
  };

  const minimumY = Math.min(
    ...series
      .filter((n) => filterOnPrioritySeries(n, filter))
      .map((n) => minMaxY[n][0])
  );
  const maximumY = Math.max(
    ...series
      .filter((n) => filterOnPrioritySeries(n, filter))
      .map((n) => minMaxY[n][1])
  );

  const sortedSeries = [
    Series.series04,
    Series.series02,
    Series.series03,
    Series.series01,
  ];

  return (
    <Track>
      <Wrapper>
        <TimeSeries<Series>
          entities={entities}
          keys={keys}
          filter={filter}
          disabled={disabled}
          maximumX={maximumX}
          maximumY={maximumY}
          minimumX={minimumX}
          minimumY={minimumY > 0 ? 0 : minimumY}
          periods={periods}
          series={seriesWithTranslation}
          seriesColorSelect={(series) => colors[series]}
          pastLabel={labels.past}
          futureLabel={labels.future}
          formatYAxis={(value) => {
            if (filter[Series.series01]) {
              return toPercentage(value, 1);
            }
            if (filter[Series.series02]) {
              return toCurrencyTruncated(value, 3, currencyISO);
            }
            if (filter[Series.series04]) {
              return toCurrencyTruncated(value, 3, divPaymentDetailCurrencyISO);
            }
            return toCurrencyTruncated(value, 3, currencyISO);
          }}
          onLegendClick={(series) => {
            trackEvent({
              legend: series,
              action: 'click',
              isVisible: !filter[series],
            });
            dispatch({
              type: 'filter',
              payload: {
                key: series,
                data,
              },
            });
          }}
          marker={({ key, xScale, yScale }) => {
            const entity = entities[key];
            const x = xScale(key);
            return (
              entity &&
              series
                .filter((n) => filter[n] && entity[n] !== undefined)
                .map((series) => {
                  let nextYScale = yScale;
                  if (series !== Series.series01) {
                    const minY = minMaxY[series][0];
                    nextYScale = yScale
                      .copy()
                      .domain([minY > 0 ? 0 : minY, minMaxY[series][1]])
                      .nice();
                  }
                  return (
                    <circle
                      key={`data-point-marker-${series}`}
                      transform={`translate(${x} ${nextYScale(
                        entity[series]
                      )})`}
                      r="4"
                      fill={colors[series]}
                      stroke="white"
                      strokeWidth="1"
                    />
                  );
                })
            );
          }}
          dataTable={({ key: date }) => {
            const entity = entities[date];
            return (
              <DataTable noWrap>
                <thead>
                  <tr>
                    <th colSpan={2}>
                      {date
                        ? dateFormatter(date, 'MMM dd yyyy')
                        : labels.notApplicable}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {sortedSeries
                    .filter((n) => filter[n])
                    .map((key) => {
                      const value =
                        entity !== undefined && entity[key] !== undefined
                          ? entity[key]
                          : undefined;
                      const postfix =
                        key === Series.series04
                          ? ` ${divPaymentDetailFreq}`
                          : labels.perYear;

                      const label = seriesWithTranslation.find(
                        (item) => item.key === key
                      );
                      return (
                        <tr key={`data-table-row-${key}`}>
                          <Title
                            data-cy-id={`chart-stability-and-growth-of-payments-datatable-title-${key}`}
                          >
                            {label?.label || key}
                          </Title>
                          <Value
                            data-cy-id={`chart-stability-and-growth-of-payments-datatable-value-${key}`}
                            noData={value === undefined}
                            value={value}
                            colorPos={colors[key]}
                            colorNeg={theme.color.bad}
                            postfix={postfix}
                          >
                            {value !== undefined &&
                              getValue(
                                key,
                                value,
                                currencyISO,
                                divPaymentDetailCurrencyISO
                              )}
                          </Value>
                        </tr>
                      );
                    })}
                </tbody>
              </DataTable>
            );
          }}
        >
          {({ xScale, yScale, maximumX, zeroX, keys, entities }) =>
            sortedSeries
              .filter((n) => filter[n])
              .map((key) => {
                let nextYScale = yScale;
                if (key !== Series.series01) {
                  const minY = minMaxY[key][0];
                  nextYScale = yScale
                    .copy()
                    .domain([minY > 0 ? 0 : minY, minMaxY[key][1]])
                    .nice();
                }
                return (
                  <g key={`chart-${key}`}>
                    {key === Series.series01 && (
                      <Area
                        data={keys.filter(
                          (d) => entities[d][key] !== undefined
                        )}
                        xSelect={(d) => d}
                        ySelect={(d) => entities[d][key] || 0}
                        xScale={xScale}
                        yScale={nextYScale}
                        fillPos={gradients[Series.series01].fillPositive}
                        fillNeg={gradients[Series.series01].fillNegative}
                        endX={zeroX}
                      />
                    )}
                    {key === Series.series01 && zeroX !== maximumX && (
                      <Area
                        data={keys.filter(
                          (d) => entities[d][key] !== undefined
                        )}
                        xSelect={(d) => d}
                        ySelect={(d) => entities[d][key] || 0}
                        xScale={xScale}
                        yScale={nextYScale}
                        fillPos={gradients[Series.series01].fillFuturePositive}
                        fillNeg={gradients[Series.series01].fillFutureNegative}
                        startX={zeroX}
                      />
                    )}
                    {key !== Series.series04 && (
                      <Line
                        data={keys.filter(
                          (d) => entities[d][key] !== undefined
                        )}
                        xSelect={(d) => d}
                        ySelect={(d) => entities[d][key] || 0}
                        xScale={xScale}
                        yScale={nextYScale}
                        strokePos={colors[key]}
                        strokeNeg={theme.color.bad}
                      />
                    )}
                    {key === Series.series04 && (
                      <Bar
                        data={keys.filter(
                          (d) => entities[d][key] !== undefined
                        )}
                        xSelect={(d) => d}
                        ySelect={(d) => entities[d][key] || 0}
                        xScale={xScale}
                        yScale={nextYScale}
                        fillPos={colors[key]}
                        fillNeg={theme.color.bad}
                        maxWidth={10}
                      />
                    )}
                  </g>
                );
              })
          }
        </TimeSeries>
      </Wrapper>
    </Track>
  );
};

StabilityAndGrowthOfPayments.defaultProps = {
  defaultFilter: {
    [Series.series01]: true,
    [Series.series02]: true,
    [Series.series03]: false,
    [Series.series04]: true,
  },
  hideToggleFilter: false,
};

export default StabilityAndGrowthOfPayments;
